/**
 * Lexo Max — jonli til materiallari (demo `MAXG`, `MAXF`, `MAXL`, `Max`, `maxHeader`, `MaxDetail`, `Tariffs`).
 * `Max` — «Kutubxona» tabi; `MaxDetail` va `Tariffs` — stek ekranlari (`.sbar/.sbody/.sfoot` o'zlari chizadi).
 */
import { LANG } from '../data';
import { nav, type MaxKey, type Tab } from '../lib/nav';
import { toast } from '../lib/toast';
import { bootstrap, sync } from '../lib/sync';
import { isInTelegram, payInvoice } from '../lib/telegram';
import { t } from '../i18n';
import { Ic } from '../ui/Icon';

export const MAXG: Record<MaxKey, [string, string]> = { radio: ['#3f78a3', '#7cc4fa'], books: ['#2e7a55', '#5fbf90'], shadow: ['#F97316', '#FFC629'] };

export interface MaxFeature { n: string; s: string; i: string; bg: string; fg: string; d: string; feats: string[]; items: Array<[string, string, string]> }

const ru = LANG === 'ru';
export const MAXF: Record<MaxKey, MaxFeature> = {
  radio: {
    n: 'Lexo Radio', s: 'Podkastlar', i: 'podcast', bg: 'var(--info-soft)', fg: 'var(--info-ink)',
    d: 'Darajangizga mos qisqa podkastlar. Subtitr bilan tinglang, tezlikni sekinlashtiring, notanish soʻzni bosib lugʻatingizga qoʻshing.',
    feats: ['Subtitr va oʻzbekcha tarjima', '0.75× sekin ijro', 'Soʻzni bosib kartochkaga qoʻshish', 'Har epizod oxirida 5 savollik test'],
    items: ru
      ? [['A1', 'Знакомство · tanishuv', '6 daq'], ['A1', 'В магазине · doʻkonda', '8 daq'], ['A2', 'Поездка в Самарканд · sayohat', '12 daq'], ['A2', 'Погода и времена года', '10 daq'], ['B1', 'Собеседование на работу', '15 daq'], ['B1', 'Жизнь в большом городе', '18 daq']]
      : [['A1', 'Salomlashish va tanishuv', '6 daq'], ['A1', 'Doʻkonda xarid', '8 daq'], ['A2', 'Sayohatdagi kun', '12 daq'], ['A2', 'Ob-havo va fasllar', '10 daq'], ['B1', 'Ish suhbatiga tayyorgarlik', '15 daq'], ['B1', 'Shahar hayoti haqida suhbat', '18 daq']],
  },
  books: {
    n: 'Kitob javoni', s: 'Darajali kitoblar', i: 'library', bg: 'var(--success-soft)', fg: 'var(--success-ink)',
    d: 'Mashhur asarlarning darajangizga moslashtirilgan versiyalari. Audio bilan birga oʻqing, har bob oxirida yangi soʻzlarni yodlang.',
    feats: [ru ? 'A1 dan B2 gacha moslashtirilgan matn (urgʻu belgilari bilan)' : 'A1 dan B2 gacha moslashtirilgan matn', 'Diktor ovozi bilan oʻqish', 'Soʻzni bosib tarjima', 'Bob oxirida soʻzlar mashqi'],
    items: ru
      ? [['A1', 'Репка · xalq ertagi', '8 bet'], ['A1', 'Колобок · xalq ertagi', '10 bet'], ['A2', 'Филиппок · L. N. Tolstoy', '14 bet'], ['A2', 'Каштанка · A. P. Chexov', '30 bet'], ['B1', 'Капитанская дочка · A. S. Pushkin', '52 bet']]
      : [['A1', 'The Little Red Hen · xalq ertagi', '12 bet'], ['A2', 'The Happy Prince · Oscar Wilde', '24 bet'], ['A2', 'The Ugly Duckling · H. C. Andersen', '20 bet'], ['B1', 'Alice in Wonderland · Lewis Carroll', '48 bet'], ['B1', 'Sherlock Holmes hikoyalari · A. Conan Doyle', '56 bet']],
  },
  shadow: {
    n: 'Kino-takror', s: 'Shadowing', i: 'clap', bg: 'var(--peach-soft)', fg: 'var(--peach-ink)',
    d: 'Filmlardan qisqa sahnalarni koʻring va qahramon bilan bir vaqtda takrorlang. Talaffuz, urgʻu va ohangni tabiiy holda oʻrganasiz.',
    feats: ['5–15 soniyalik sahnalar', 'Gapma-gap takrorlash', 'Ovozingizni yozib, asl bilan solishtirish', 'Talaffuz bahosi'],
    items: ru
      ? [['A2', 'Ochiq litsenziyali multfilm (rus tilida) · 1-sahna', '8 soniya'], ['A2', 'Разговор в кафе · kundalik suhbat', '12 soniya'], ['B1', 'На вокзале · chipta olish', '10 soniya'], ['B1', 'Klassik film · tanishuv sahnasi', '14 soniya']]
      : [['A2', 'Ochiq litsenziyali animatsion film · 1-sahna', '8 soniya'], ['A2', 'Kundalik suhbat · kafeda', '12 soniya'], ['B1', 'Ilmiy-fantastik qisqa film · 3-sahna', '10 soniya'], ['B1', 'Klassik film · tanishuv sahnasi', '14 soniya']],
  },
};

export const MAXL: { cnt: [string, string, string]; feat: [string, string]; cont: Array<[MaxKey, string, string, number]> } = ru
  ? {
    cnt: ['60+ epizod', '25 ta kitob', '40 sahna'], feat: ['Каштанка', 'A. P. Chexov · A2 · 30 bet'],
    cont: [['books', 'Репка', 'Kitob · 2-bob', 42], ['radio', 'В магазине', 'Podkast · 5:10 / 8:00', 63], ['shadow', 'Разговор в кафе', 'Kino-takror · 3/8 gap', 37]],
  }
  : {
    cnt: ['120+ epizod', '40 ta kitob', '80 sahna'], feat: ['The Happy Prince', 'Oscar Wilde · A2 · 24 bet'],
    cont: [['books', 'Alice in Wonderland', 'Kitob · 3-bob', 42], ['radio', 'Sayohatdagi kun', 'Podkast · 7:40 / 12:00', 63], ['shadow', 'Kafedagi suhbat', 'Kino-takror · 3/8 gap', 37]],
  };

const grad = (k: MaxKey, deg = 160) => `linear-gradient(${deg}deg,${MAXG[k][0]},${MAXG[k][1]})`;

/** Demo `big3b` kartalari (Kutubxona va Bosh sahifada). */
export function Big3({ onOpen }: { onOpen: (k: MaxKey) => void }) {
  const big: Array<[MaxKey, string, number]> = [['radio', 'Podkastlar', 0], ['books', 'Kitoblar', 1], ['shadow', 'Kino-takror', 2]];
  return (
    <div className="big3b">
      {big.map(([k, l, n]) => (
        <button key={k} style={{ background: grad(k) }} onClick={() => onOpen(k)}>
          <Ic n={MAXF[k].i} className="i bgi" />
          <i><Ic n={MAXF[k].i} /></i>
          <b>{t(l)}</b><small>{t(MAXL.cnt[n]!)}</small>
        </button>
      ))}
    </div>
  );
}

export function Max() {
  return (
    <>
      <div className="row g10">
        <b className="grow" style={{ fontSize: 24, fontWeight: 800 }}>{t('Kutubxona')}</b>
        <span className="maxb">MAX</span>
        <button className="sq2 card" aria-label={t('Qidirish')} onClick={() => toast(t('Qidiruv Max tarifda ochiladi'))}><Ic n="search" size={20} /></button>
      </div>
      <Big3 onOpen={(k) => nav.push({ k: 'maxDetail', key: k })} />
      <button className="feat2" onClick={() => nav.push({ k: 'maxDetail', key: 'books' })}>
        <span className="cv"><Ic n="book" size={40} /></span>
        <span className="col g4" style={{ padding: 14, textAlign: 'left' }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: '#F97316', letterSpacing: '.05em' }}>{t('HAFTANING KITOBI')}</span>
          <b style={{ fontSize: 18 }}>{t(MAXL.feat[0])}</b>
          <span className="t13">{t(MAXL.feat[1])}</span>
          <span className="pill2"><Ic n="play" size={12} style={{ fill: '#fff' }} />{t('Oʻqishni boshlash')}</span>
        </span>
      </button>
      <b className="t16b" style={{ fontSize: 18 }}>{t('Davom ettiring')}</b>
      <div className="col g10">
        {MAXL.cont.map(([k, tt, s, p]) => (
          <button key={k} className="cont2" onClick={() => nav.push({ k: 'maxDetail', key: k })}>
            <span className="sq2" style={{ background: grad(k), color: '#fff' }}><Ic n={MAXF[k].i} /></span>
            <span className="grow col" style={{ textAlign: 'left' }}>
              <b style={{ fontSize: 14 }}>{t(tt)}</b><span className="t12">{t(s)}</span>
              <span className="trk2"><i style={{ width: `${p}%` }} /></span>
            </span>
            <span className="play2"><Ic n="lock" size={14} /></span>
          </button>
        ))}
      </div>
      <button className="tarif" onClick={() => nav.push({ k: 'tariffs' })}>
        <span className="grow col g4" style={{ textAlign: 'left' }}><b className="t15b">{t('Tariflarni solishtirish')}</b><span className="t13">{t('Bepul · Pro · Max')}</span></span>
        <Ic n="chevRight" size={20} />
      </button>
      <p className="t12" style={{ margin: 0 }}>{t('Test versiyada bu boʻlim namuna sifatida koʻrsatilgan — Max tarifda ochiladi.')}</p>
    </>
  );
}

/** Demo `maxHeader` — stek ekran sarlavhasi (orqaga + MAX belgisi). */
function MaxHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="sbar">
      <button className="x" aria-label={t('Orqaga')} onClick={onBack}><Ic n="back" /></button>
      <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{title}</b>
      <span className="maxb">MAX</span>
    </div>
  );
}

export function MaxDetail({ k, from }: { k: MaxKey; from?: Tab }) {
  const f = MAXF[k];
  return (
    <>
      <MaxHeader title={t(f.n)} onBack={() => (from ? nav.home(from) : nav.pop())} />
      <div className="sbody fade" style={{ gap: 16 }}>
        <div className="maxhero" style={{ background: grad(k, 135), color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <Ic n={f.i} style={{ position: 'absolute', right: -14, bottom: -18, width: 110, height: 110, opacity: .16, stroke: '#fff' }} />
          <span className="mhi"><Ic n={f.i} size={30} /></span>
          <div className="col g4"><b style={{ fontSize: 22, lineHeight: '28px', fontWeight: 800 }}>{t(f.n)}</b><span className="t13" style={{ color: '#fff', opacity: .9, fontWeight: 700 }}>{t(f.s)}</span></div>
        </div>
        <p className="sub" style={{ margin: 0 }}>{t(f.d)}</p>
        <div className="col g8">
          {f.feats.map((x) => <span key={x} className="row g8 t13" style={{ color: 'var(--ink)', fontWeight: 600 }}><span className="ck" style={{ width: 20, height: 20 }}><Ic n="check" /></span>{t(x)}</span>)}
        </div>
        <div className="col g8">
          <span className="cap">{t('Namuna roʻyxat')}</span>
          <div className="wl">
            {f.items.map(([lv, tt, dur]) => (
              <div className="wr" key={tt}>
                <span className="badge" style={{ width: 36, height: 36, borderRadius: 12, fontSize: 12, background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}>{lv}</span>
                <div className="grow"><b>{t(tt)}</b><span>{t(dur)}</span></div>
                <span className="lockb"><Ic n="lock" size={16} /></span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="sfoot"><button className="btn" style={{ background: 'linear-gradient(90deg,#1A66F5,#5B93FF)' }} onClick={() => nav.push({ k: 'tariffs' })}>{t('Max tarifda ochiladi')}</button></div>
    </>
  );
}

const PLANS: Array<[string, string, string[], boolean]> = [
  ['Bepul', '0 soʻm', ['Kuniga 5 ta yangi soʻz', 'Takrorlash cheksiz', 'Asosiy mashqlar'], false],
  ['Pro', '19 900 soʻm / oy', ['Cheklanmagan soʻzlar', 'Barcha oʻyinlar', 'Kartochkalar va qiyin soʻzlar', 'Batafsil statistika'], false],
  ['Max', '39 900 soʻm / oy', ['Pro’dagi hamma narsa', 'Lexo Radio — podkastlar', 'Kitob javoni — darajali kitoblar', 'Kino-takror — shadowing', 'Talaffuz tahlili'], true],
];

/** Telegram Stars orqali Max (1 oy): API invoice havolasi → Telegram to'lov oynasi. Telegram'dan tashqarida — xabar. */
async function buyMax() {
  if (!isInTelegram()) { toast(t('Toʻlov Telegram ichida — Stars orqali')); return; }
  try {
    const { url } = await sync.invoice('max', 1);
    const status = await payInvoice(url);
    if (status === 'paid') { toast(t('Toʻlov qabul qilindi — Max faollashdi')); await bootstrap(); nav.pop(); }
    else if (status === 'cancelled') toast(t('Toʻlov bekor qilindi'));
  } catch (e) {
    toast(e instanceof Error ? e.message : t('Toʻlovda xato'));
  }
}

export function Tariffs() {
  return (
    <>
      <MaxHeader title={t('Tariflar')} onBack={() => nav.pop()} />
      <div className="sbody fade" style={{ gap: 12 }}>
        {PLANS.map(([n, pr, fs, hi]) => (
          <div key={n} className={`plan ${hi ? 'hi' : ''}`}>
            <div className="between"><b style={{ fontSize: 20, fontWeight: 800 }}>{t(n)}</b>{hi && <span className="maxb">{t('Eng toʻliq')}</span>}</div>
            <b style={{ fontSize: 18, color: 'var(--primary-ink)' }}>{t(pr)}</b>
            <div className="col g6">
              {fs.map((x) => <span key={x} className="row g8 t13" style={{ color: 'var(--ink)', fontWeight: 600 }}><span className="ck" style={{ width: 18, height: 18 }}><Ic n="check" /></span>{t(x)}</span>)}
            </div>
          </div>
        ))}
        <p className="t12" style={{ margin: 0 }}>{t('Narxlar taxminiy. Test versiyada toʻlov yoʻq.')}</p>
      </div>
      <div className="sfoot"><button className="btn" onClick={() => void buyMax()}>{t('Maxʼni sinab koʻrish')}</button></div>
    </>
  );
}
